import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { translate } from "../../../hooks/translate";
import { useTitle } from "../../../hooks/useTitle";
import { formatInstantDateShortTr } from "../../../utils/appTime";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Input from "../../../components/BaseUI/Input";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import {
  FiCpu,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiRefreshCw,
  FiUser,
} from "react-icons/fi";
import "./Machines.scss";
import Label from "../../../components/BaseUI/Label";
import { withAuth } from "../../../components/HOC/withAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const getMqttHardwareIdForMachine = (machine) => {
  const fromApi = String(machine?.hardwareId || "").trim();
  if (fromApi) return fromApi;
  const hw = machine?.hardwareDeviceId;
  if (hw && typeof hw === "object") {
    return String(hw.hardwareId || hw.serialNumber || "").trim();
  }
  return "";
};

const normalizeMongoId = (v) => {
  if (v == null || v === "") return "";
  if (typeof v === "object" && v._id != null) return String(v._id);
  if (typeof v === "object" && v.$oid != null) return String(v.$oid);
  return String(v);
};

const Machines = () => {
  useTitle("Rovion MES - Makineler");
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(currentUser.role, "/machines", currentUser.pages || [], !!currentUser.fullAccess);

  const [machines, setMachines] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    machineId: null,
  });
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    hardwareDeviceId: "",
    location: "",
    notes: "",
    entryType: "device",
    isActive: true,
  });

  const headers = [
    {
      name: "Makine Adı",
      dataType: "text",
      id: 0,
      customSorter: (a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")),
    },
    {
      name: "Makine Kodu",
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        String(a?.code || "").localeCompare(String(b?.code || "")),
    },
    {
      name: "Veri Girişi",
      dataType: "text",
      id: 2,
      customSorter: (a, b) =>
        String(a?.entryType || "").localeCompare(String(b?.entryType || "")),
    },
    {
      name: "Bağlı Cihaz",
      dataType: "text",
      id: 3,
      customSorter: (a, b) => {
        const label = (m) => {
          const hw = m?.hardwareDeviceId;
          if (!hw || typeof hw !== "object") return "";
          return String(hw.serialNumber || hw.name || m?.hardwareId || "");
        };
        return label(a).localeCompare(label(b));
      },
    },
    {
      name: "Konum",
      dataType: "text",
      id: 4,
      customSorter: (a, b) =>
        String(a?.location || "").localeCompare(String(b?.location || "")),
    },
    {
      name: "Notlar",
      dataType: "text",
      id: 5,
      customSorter: (a, b) =>
        String(a?.notes || "").localeCompare(String(b?.notes || "")),
    },
    {
      name: "Durum",
      dataType: "text",
      id: 6,
      customSorter: (a, b) => Number(!!b?.isActive) - Number(!!a?.isActive),
    },
    {
      name: "Oluşturulma",
      dataType: "date",
      id: 7,
      customSorter: (a, b) =>
        new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0),
    },
    { name: "İşlemler", id: 8, sortable: false },
  ];

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("machines-list", headers);

  const addToast = (message, type = "info") => {
    const newToast = {
      id: Date.now(),
      message,
      type,
    };
    setToasts([...toasts, newToast]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== newToast.id));
    }, 3000);
  };

  useEffect(() => {
    fetchMachines();
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/hardware-devices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data?.hardwareDevices || response.data;
      setDevices(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch devices:", error);
      setDevices([]);
    }
  };

  const fetchMachines = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/machines`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const machinesData = response.data?.machines || response.data;
      setMachines(Array.isArray(machinesData) ? machinesData : []);
    } catch (error) {
      console.error("Failed to fetch machines:", error);
      addToast("Makineler yüklenemedi", "danger");
      setMachines([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingMachine(null);
    setFormData({
      name: "",
      code: "",
      hardwareDeviceId: "",
      location: "",
      notes: "",
      entryType: "device",
      isActive: true,
    });
  };

  const handleCreate = () => {
    resetForm();
    setSidebarOpened(true);
  };

  const handleEdit = (machine) => {
    setEditingMachine(machine);
    setFormData({
      name: machine.name,
      code: machine.code,
      hardwareDeviceId:
        machine.hardwareDeviceId?._id || machine.hardwareDeviceId || "",
      location: machine.location,
      notes: machine.notes || "",
      entryType: machine.entryType || "device",
      isActive: machine.isActive !== false,
    });
    setSidebarOpened(true);
  };

  const handleClose = () => {
    setSidebarOpened(false);
    resetForm();
  };

  const handleDelete = (machineId) => {
    setConfirmModal({ isOpen: true, machineId });
  };

  const confirmDelete = async () => {
    if (!confirmModal.machineId) return;

    try {
      const token = getToken();
      await axios.delete(`${API_URL}/api/machines/${confirmModal.machineId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      addToast("Makine başarıyla silindi", "success");
      setConfirmModal({ isOpen: false, machineId: null });
      fetchMachines();
    } catch (error) {
      console.error("Failed to delete machine:", error);
      addToast(error.response?.data?.message || "Makine silinemedi", "danger");
      setConfirmModal({ isOpen: false, machineId: null });
    }
  };

  const cancelDelete = () => {
    setConfirmModal({ isOpen: false, machineId: null });
  };

  const handleUpdateUserList = async (machine) => {
    const mqttId = getMqttHardwareIdForMachine(machine);
    if (!mqttId) {
      addToast("Makine Hardware ID'si bulunamadı", "warning");
      return;
    }

    try {
      const token = getToken();
      await axios.post(
        `${API_URL}/api/machines/${encodeURIComponent(mqttId)}/command`,
        { action: "update_userlist" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      addToast("Kullanıcı listesi güncelleme komutu gönderildi", "success");
    } catch (error) {
      console.error("Failed to send update_user_list command:", error);
      addToast(
        error.response?.data?.message || "Komut gönderilemedi",
        "danger",
      );
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) {
      addToast("Lütfen tüm zorunlu alanları doldurun", "warning");
      return;
    }
    const hasDevice = String(formData.hardwareDeviceId || "").trim();
    if (hasDevice) {
      const selectedDevice = devices.find(
        (d) =>
          normalizeMongoId(d._id ?? d.id) ===
          normalizeMongoId(formData.hardwareDeviceId),
      );
      if (!selectedDevice || !isDeviceAssignable(selectedDevice)) {
        addToast("Bu cihaz başka bir makineye bağlı", "warning");
        return;
      }
    }

    try {
      const token = getToken();

      const payload = {
        name: formData.name,
        code: formData.code,
        hardwareDeviceId: hasDevice ? formData.hardwareDeviceId : "",
        location: formData.location,
        notes: formData.notes,
        entryType: formData.entryType || "device",
        isActive: formData.isActive,
      };

      if (editingMachine) {
        await axios.put(
          `${API_URL}/api/machines/${editingMachine._id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        addToast("Makine başarıyla güncellendi", "success");
      } else {
        await axios.post(`${API_URL}/api/machines`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        addToast("Makine başarıyla oluşturuldu", "success");
      }

      setSidebarOpened(false);
      resetForm();
      fetchMachines();
    } catch (error) {
      console.error("Failed to save machine:", error);
      addToast(
        error.response?.data?.message || "Makine kaydedilemedi",
        "danger",
      );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return formatInstantDateShortTr(dateString);
  };

  const filteredMachines = Array.isArray(machines)
    ? machines.filter((machine) => {
        const query = searchQuery.toLowerCase();
        return (
          machine?.name?.toLowerCase().includes(query) ||
          machine?.code?.toLowerCase().includes(query) ||
          machine?.location?.toLowerCase().includes(query)
        );
      })
    : [];

  /** Use machines list only — hardware-devices `machines` populate was hiding free devices incorrectly */
  const isDeviceAssignable = (device) => {
    if (!device) return false;
    const did = normalizeMongoId(device._id ?? device.id);
    if (!did) return false;
    const occupant = machines.find((m) => {
      const assigned = normalizeMongoId(m.hardwareDeviceId);
      return !!assigned && assigned === did;
    });
    if (!occupant) return true;
    if (
      editingMachine &&
      normalizeMongoId(occupant._id) ===
        normalizeMongoId(editingMachine._id)
    ) {
      return true;
    }
    return false;
  };

  const assignableDevices = Array.isArray(devices)
    ? devices.filter((d) => isDeviceAssignable(d))
    : [];

  return (
    <AppPage
      title="Makineler"
      subtitle="Makine kayıtlarını görüntüleyin ve yönetin"
      icon={<FiCpu />}
      actions={
        canWrite
          ? [
              <Button
                key="add"
                appearance="primary-animated"
                onClick={handleCreate}
                icon={<FiPlus />}
              >
                Yeni Makine
              </Button>,
            ]
          : undefined
      }
    >
      <CardContainer>
        {loading ? (
          <div className="loading-container">
            <FiCpu className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        ) : (
          <>
            <Table
              columns={headers}
              visibleColumns={visibleColumns}
              tableId="machines-list"
              checkboxSelection
              loading={loading}
              viewOptions={
                <ListViewOptionsPanel
                  columns={headers}
                  visibleColumns={visibleColumns}
                  onToggleColumn={toggleColumnVisibility}
                  onResetColumns={resetToDefault}
                  showApply={false}
                  showReset={false}
                />
              }
            >
              {Array.isArray(filteredMachines) &&
                filteredMachines.map((machine) => (
                  <Tr key={machine._id} id={machine._id} data={machine}>
                    {visibleColumns.includes(0) && (
                      <Td>
                        <strong>{machine.name}</strong>
                      </Td>
                    )}
                    {visibleColumns.includes(1) && <Td>{machine.code}</Td>}
                    {visibleColumns.includes(2) && (
                      <Td>{machine.entryType === "epc" ? "EPC" : "Cihaz"}</Td>
                    )}
                    {visibleColumns.includes(3) && (
                      <Td>
                        {(() => {
                          const hw = machine.hardwareDeviceId;
                          if (!hw || typeof hw !== "object") return "-";
                          const label =
                            String(hw.serialNumber || hw.name || "").trim() ||
                            "-";
                          const hwId = String(machine.hardwareId || "").trim();
                          return (
                            <div className="d-flex flex-column">
                              <span>{label}</span>
                              {hwId && hwId !== label && (
                                <span className="machine-device-hwid">{hwId}</span>
                              )}
                            </div>
                          );
                        })()}
                      </Td>
                    )}
                    {visibleColumns.includes(4) && <Td>{machine.location}</Td>}
                    {visibleColumns.includes(5) && (
                      <Td>{machine.notes || "-"}</Td>
                    )}
                    {visibleColumns.includes(6) && (
                      <Td>
                        {machine.isActive !== false ? (
                          <span className="badge badge-success-subtle">Aktif</span>
                        ) : (
                          <span className="badge badge-danger-subtle">Pasif</span>
                        )}
                      </Td>
                    )}
                    {visibleColumns.includes(7) && (
                      <Td>{formatDate(machine.createdAt)}</Td>
                    )}
                    {visibleColumns.includes(8) && (
                      <Td>
                        <div className="d-flex align-items-center gap-1">
                          {canWrite && (
                            <>
                              <IconButton
                                icon={<FiUser style={{ fontSize: "18px" }} />}
                                appearance="subtle"
                                shape="circle"
                                onClick={() => handleUpdateUserList(machine)}
                                title="Kullanıcı Listesini Güncelle"
                              />
                              <IconButton
                                icon={<FiEdit2 style={{ fontSize: "18px" }} />}
                                appearance="subtle"
                                shape="circle"
                                onClick={() => handleEdit(machine)}
                              />
                              <IconButton
                                icon={<FiTrash2 style={{ fontSize: "18px" }} />}
                                appearance="subtle"
                                shape="circle"
                                onClick={() => handleDelete(machine._id)}
                              />
                            </>
                          )}
                        </div>
                      </Td>
                    )}
                  </Tr>
                ))}
            </Table>

            {(!filteredMachines || filteredMachines.length === 0) && (
              <div className="empty-state">
                <FiCpu size={64} />
                <p>Makine bulunamadı</p>
              </div>
            )}
          </>
        )}
      </CardContainer>

      <SidebarContainer
        show={sidebarOpened}
        title={editingMachine ? "Makineyi Düzenle" : "Yeni Makine Ekle"}
        onClose={handleClose}
      >
        <div className="d-flex flex-column" style={{ height: "100%" }}>
          <div className="machine-form">
            <div className="mt-2">
              <Label className="mb-0">Makine Adı *</Label>
              <Input
                type="text"
                placeholder="Makine adını girin"
                value={formData.name}
                onChange={(value) => setFormData({ ...formData, name: value })}
              />
            </div>
            <div className="mt-2">
              <Label className="mb-0">Makine Kodu *</Label>
              <Input
                type="text"
                placeholder="Makine kodunu girin"
                value={formData.code}
                onChange={(value) => setFormData({ ...formData, code: value })}
              />
            </div>
            <div className="mt-2">
              <Label className="mb-0 required">Veri Giriş Tipi</Label>
              <div style={{ border: "1px solid #d0d5dd" }}>
                <Dropdown
                  trigger={
                    <div
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        backgroundColor: "#fff",
                        padding: "2px 4px",
                      }}
                    >
                      <span style={{ fontSize: 14 }}>
                        {formData.entryType === "epc" ? "EPC" : "Cihaz"}
                      </span>
                    </div>
                  }
                  customTrigger={false}
                  hideDropdownIcon={false}
                  closeOnSelect={true}
                >
                  <DropdownContent>
                    <DropdownItem
                      onClick={() =>
                        setFormData({ ...formData, entryType: "device" })
                      }
                      active={formData.entryType !== "epc"}
                    >
                      Cihaz
                    </DropdownItem>
                    <DropdownItem
                      onClick={() =>
                        setFormData({
                          ...formData,
                          entryType: "epc",
                          hardwareDeviceId: "",
                        })
                      }
                      active={formData.entryType === "epc"}
                    >
                      EPC
                    </DropdownItem>
                  </DropdownContent>
                </Dropdown>
              </div>
            </div>
            {formData.entryType !== "epc" && (
              <div className="mt-2">
                <Label className="mb-0">Bağlı Cihaz</Label>
                <div style={{ border: "1px solid #d0d5dd" }}>
                  <Dropdown
                    trigger={
                      <div
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          backgroundColor: "#fff",
                          padding: "2px 4px",
                        }}
                      >
                        <span style={{ fontSize: 14 }}>
                          {formData.hardwareDeviceId
                            ? (() => {
                                const d = devices.find(
                                  (dev) =>
                                    normalizeMongoId(dev._id ?? dev.id) ===
                                    normalizeMongoId(formData.hardwareDeviceId),
                                );
                                return d ? d.name || d.hardwareId : "—";
                              })()
                            : "Bağlı Cihaz Yok"}
                        </span>
                      </div>
                    }
                    customTrigger={false}
                    hideDropdownIcon={false}
                    closeOnSelect={true}
                  >
                    <DropdownContent>
                      <DropdownItem
                        onClick={() =>
                          setFormData({ ...formData, hardwareDeviceId: "" })
                        }
                        active={!String(formData.hardwareDeviceId || "").trim()}
                      >
                        Bağlı Cihaz Yok
                      </DropdownItem>
                      {assignableDevices.length === 0 ? (
                        <div
                          style={{
                            padding: "8px 12px",
                            fontSize: 14,
                            color: "#667085",
                          }}
                        >
                          Bağlanabilir cihaz yok
                        </div>
                      ) : (
                        assignableDevices.map((device) => (
                          <DropdownItem
                            key={device._id ?? device.id}
                            onClick={() =>
                              setFormData({
                                ...formData,
                                hardwareDeviceId: device._id ?? device.id,
                              })
                            }
                            active={
                              normalizeMongoId(formData.hardwareDeviceId) ===
                              normalizeMongoId(device._id ?? device.id)
                            }
                          >
                            {device.name || device.hardwareId}
                          </DropdownItem>
                        ))
                      )}
                    </DropdownContent>
                  </Dropdown>
                </div>
              </div>
            )}
            <div className="mt-2">
              <Label className="mb-0">Konum *</Label>
              <Input
                type="text"
                placeholder="Makine konumunu girin"
                value={formData.location}
                onChange={(value) =>
                  setFormData({ ...formData, location: value })
                }
              />
            </div>
            <div className="form-group mb-3 mt-2">
              <Label className="mb-0">Notlar</Label>
              <textarea
                className="form-control"
                placeholder="Ek notlar"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                rows={3}
              />
            </div>
            <div className="mt-2">
              <Label className="mb-0 required">Durum</Label>
              <div style={{ border: "1px solid #d0d5dd" }}>
                <Dropdown
                  trigger={
                    <div
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        backgroundColor: "#fff",
                        padding: "2px 4px",
                      }}
                    >
                      <span style={{ fontSize: 14 }}>
                        {formData.isActive ? "Aktif" : "Pasif"}
                      </span>
                    </div>
                  }
                  customTrigger={false}
                  hideDropdownIcon={false}
                  closeOnSelect={true}
                >
                  <DropdownContent>
                    <DropdownItem
                      onClick={() =>
                        setFormData({ ...formData, isActive: true })
                      }
                      active={formData.isActive}
                    >
                      Aktif
                    </DropdownItem>
                    <DropdownItem
                      onClick={() =>
                        setFormData({ ...formData, isActive: false })
                      }
                      active={!formData.isActive}
                    >
                      Pasif
                    </DropdownItem>
                  </DropdownContent>
                </Dropdown>
              </div>
            </div>
          </div>
          <div className="mt-4 d-flex gap-2">
            <Button appearance="danger-subtle" onClick={handleClose}>
              İptal
            </Button>
            <Button appearance="primary-animated" onClick={handleSubmit}>
              {editingMachine ? "Güncelle" : "Oluştur"}
            </Button>
          </div>
        </div>
      </SidebarContainer>

      <ToastContainer>
        {Array.isArray(toasts) &&
          toasts.map((toast) => (
            <Toast
              key={toast.id}
              title={toast.message}
              appearance={toast.type}
              onClose={() =>
                setToasts((prev) => prev.filter((t) => t.id !== toast.id))
              }
            />
          ))}
      </ToastContainer>

      <Modal isOpen={confirmModal.isOpen} onClose={cancelDelete} width="small">
        <ModalHeader>Makineyi Sil</ModalHeader>
        <ModalBody>
          <p>
            Bu makineyi silmek istediğinizden emin misiniz? Bu işlem geri
            alınamaz.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button appearance="subtle" onClick={cancelDelete}>
            İptal
          </Button>
          <Button appearance="danger" onClick={confirmDelete}>
            Sil
          </Button>
        </ModalFooter>
      </Modal>
    </AppPage>
  );
};

export default withAuth(Machines, ["admin", "employee", "user"]);
